// Angular import
import { Component, DestroyRef, OnInit, output, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Location } from '@angular/common';
import { RouterModule } from '@angular/router';

//theme version
import { environment } from 'src/environments/environment';

// project import
import { NavigationItem, NavigationItems } from '../navigation';

import { NavCollapseComponent } from './nav-collapse/nav-collapse.component';
import { NavGroupComponent } from './nav-group/nav-group.component';
import { NavItemComponent } from './nav-item/nav-item.component';


import { SharedS } from 'src/app/shared/shared-s';
import { AuthService } from 'src/app/core/services/auth.service';
import { getSidebarAccessRule, toPermissionConfig } from 'src/app/core/config/sidebar-access';
import { combineLatest } from 'rxjs';
import { HorariosApiService } from 'src/app/features/horarios/services/horarios-api.service';
import { resolverRestriccionUsuarioMedicoContexto } from 'src/app/shared/utils/usuario-medico-restriccion.util';

@Component({
  selector: 'app-nav-content',
  imports: [RouterModule, NavCollapseComponent, NavGroupComponent, NavItemComponent, SharedS],
  templateUrl: './nav-content.component.html',
  styleUrl: './nav-content.component.scss'
})
export class NavContentComponent implements OnInit {
  private location = inject(Location);
  private authService = inject(AuthService);
  private destroyRef = inject(DestroyRef);
  private horariosApi = inject(HorariosApiService);

  // public props
  NavCollapsedMob = output();
  SubmenuCollapse = output();

  // version
  currentApplicationVersion = environment.appVersion;

  navigations!: NavigationItem[];
  windowWidth: number;

  // Constructor
  constructor() {
    this.navigations = [];
    this.windowWidth = window.innerWidth;
  }

  // Life cycle events
  ngOnInit() {
    // Filtrar menú por permisos y mantenerlo reactivo a login/logout
    this.actualizarMenu();
    this.authService.permisos$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.actualizarMenu());

    if (this.windowWidth < 1025) {
      setTimeout(() => {
        (document.querySelector('.coded-navbar') as HTMLDivElement).classList.add('menupos-static');
      }, 500);
    }
  }

  private actualizarMenu(): void {
    const baseItems = this.filtrarNavigationItems(NavigationItems);

    if (this.authService.hasRole('ADMINISTRADOR')) {
      this.navigations = baseItems;
      return;
    }

    const currentUserId = this.authService.getUsuarioId();
    if (!currentUserId) {
      this.navigations = baseItems;
      return;
    }

    combineLatest([
      this.horariosApi.listarMedicos(),
      this.horariosApi.listarUsuarios(),
      this.horariosApi.listarEmpleados(),
    ]).subscribe({
      next: ([medicos, usuarios, empleados]) => {
        const restriccion = resolverRestriccionUsuarioMedicoContexto({
          currentUserId,
          rol: this.authService.getRol(),
          esAdministrador: this.authService.hasRole('ADMINISTRADOR'),
          medicos: medicos || [],
          usuarios: usuarios || [],
          empleados: empleados || [],
        });

        this.navigations = this.personalizarMenuHorarios(baseItems, restriccion.esUsuarioMedicoRestringido);
      },
      error: () => {
        this.navigations = baseItems;
      },
    });
  }

  private personalizarMenuHorarios(items: NavigationItem[], esUsuarioMedicoRestringido: boolean): NavigationItem[] {
    if (!esUsuarioMedicoRestringido) return items;

    const personalizar = (list: NavigationItem[]): NavigationItem[] =>
      (list || []).map((it) => {
        const copy: NavigationItem = { ...it };

        if (copy.url === '/horarios/lista') {
          copy.title = 'Mi agenda médica';
        }

        if (copy.children?.length) {
          copy.children = personalizar(copy.children);
        }

        return copy;
      });

    return personalizar(items);
  }

  private filtrarNavigationItems(items: NavigationItem[]): NavigationItem[] {
    // Admin: mostrar todo lo habilitado explicitamente en el menu.
    if (this.authService.hasRole('ADMINISTRADOR')) {
      return this.stripHiddenItems(items);
    }

    const filtrarRec = (list: NavigationItem[]): NavigationItem[] => {
      return (list || [])
        .map((it) => {
          if (it.hidden) return { ...it, hidden: true };

          if (it.children && it.children.length > 0) {
            const children = filtrarRec(it.children);
            return { ...it, children, hidden: children.length === 0 };
          }

          if (!it.url) {
            return it;
          }

          const rule = getSidebarAccessRule(it.url);
          if (!rule?.modulo) {
            // Si no sabemos mapear, por seguridad lo ocultamos a no-admin
            return { ...it, hidden: true };
          }

          const permissionConfig = toPermissionConfig(rule);
          const visible = permissionConfig
            ? this.authService.tienePermisoConEntidad(permissionConfig.modulo, permissionConfig.entidad, permissionConfig.acciones)
            : this.authService.tieneAccesoModulo(rule.modulo);

          return { ...it, hidden: !visible };
        })
        .filter((it) => !it.hidden);
    };

    return filtrarRec(items);
  }

  private stripHiddenItems(items: NavigationItem[]): NavigationItem[] {
    return (items || [])
      .filter((it) => !it.hidden)
      .map((it) => {
        if (!it.children?.length) return { ...it };

        const children = this.stripHiddenItems(it.children);
        return { ...it, children, hidden: children.length === 0 };
      })
      .filter((it) => !it.hidden);
  }


  fireOutClick() {
    let current_url = this.location.path();
    // eslint-disable-next-line
    // @ts-ignore
    if (this.location['_baseHref']) {
      // eslint-disable-next-line
      // @ts-ignore
      current_url = this.location['_baseHref'] + this.location.path();
    }
    const link = "a.nav-link[ href='" + current_url + "' ]";
    const ele = document.querySelector(link);
    if (ele !== null && ele !== undefined) {
      const parent = ele.parentElement;
      const up_parent = parent?.parentElement?.parentElement;
      const last_parent = up_parent?.parentElement;
      if (parent?.classList.contains('coded-hasmenu')) {
        parent.classList.add('coded-trigger');
        parent.classList.add('active');
      } else if (up_parent?.classList.contains('coded-hasmenu')) {
        up_parent.classList.add('coded-trigger');
        up_parent.classList.add('active');
      } else if (last_parent?.classList.contains('coded-hasmenu')) {
        last_parent.classList.add('coded-trigger');
        last_parent.classList.add('active');
      }
    }
  }
}
